import React from 'react';
import PropTypes from 'prop-types';
import { Button } from "../../../common/button";
import { Loader2, HelpCircle } from "lucide-react";

const TransactionForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  loading,
  showExamples,
  setShowExamples,
  examples = [],
  additionalFields = []
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'select':
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium mb-2">
              {field.label}
              {field.required && ' *'}
            </label>
            <select
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className="w-full p-3 border rounded-md"
              required={field.required}
            >
              <option value="">선택해주세요</option>
              {field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500">{field.description}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium mb-2">
              {field.label}
              {field.required && ' *'}
            </label>
            <textarea
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className="w-full p-3 border rounded-md min-h-[100px]"
              placeholder={field.placeholder}
              required={field.required}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500">{field.description}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium mb-2">
              {field.label}
              {field.required && ' *'}
            </label>
            <input
              type={field.type || 'text'}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className="w-full p-3 border rounded-md"
              placeholder={field.placeholder}
              required={field.required}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500">{field.description}</p>
            )}
          </div>
        );
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* 예시 섹션 */}
      {showExamples ? (
        <div className="mb-6 p-4 bg-blue-50 rounded-md">
          <h4 className="font-semibold mb-2 flex items-center">
            <span className="mr-2">입력 예시</span>
            <HelpCircle className="h-4 w-4 text-blue-500" />
          </h4>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            {examples.map((example, index) => (
              <li key={index}>{example}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setShowExamples(false)}
            className="text-sm text-blue-600 mt-2"
          >
            예시 닫기
          </button>
        </div>
      ) : (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowExamples(true)}
            className="text-sm text-blue-600 flex items-center"
          >
            <HelpCircle className="h-4 w-4 mr-1" />
            예시 보기
          </button>
        </div>
      )}

      {/* 거래 내용 입력 필드 */}
      <div>
        <label className="block text-sm font-medium mb-2">
          적요 입력 *
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full p-3 border rounded-md min-h-[100px]"
          placeholder="거래 내용을 입력해주세요"
          required
        />
      </div>

      {/* 금액 입력 필드 */}
      <div>
        <label className="block text-sm font-medium mb-2">
          금액 *
        </label>
        <input
          type="text"
          name="amount"
          value={formData.amount}
          onChange={handleChange}
          className="w-full p-3 border rounded-md"
          placeholder="금액을 입력하세요"
          required
        />
      </div>

      {/* 추가 필드들 */}
      {additionalFields.map(field => renderField(field))}

      {/* 제출 버튼 */}
      <Button 
        type="submit" 
        className="w-full mt-6" 
        disabled={loading || !formData.description.trim()}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            분석 중...
          </>
        ) : (
          'Accufit에게 추천받기'
        )}
      </Button>
    </form>
  );
};

TransactionForm.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showExamples: PropTypes.bool.isRequired,
  setShowExamples: PropTypes.func.isRequired,
  examples: PropTypes.arrayOf(PropTypes.string),
  additionalFields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    description: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    }))
  }))
};

export default TransactionForm; 